import { Camera, Loader2 } from 'lucide-react';
import { MemberAvatar } from './equipeUi';
import { useLivreurPhotoUpload } from '../../utils/useLivreurPhotoUpload';

/**
 * Photo praticien compacte : avatar cliquable, sans dropzone.
 */
export default function PractitionerPhotoControl({
  member,
  commercantId,
  onPhotoChange,
  disabled = false,
  saving = false,
  /** inline = colonne photo dans la fiche Général (centré, textes réduits) */
  variant = 'default',
}) {
  const inline = variant === 'inline';
  const professionalPhoto = member?.instructeur?.photo?.trim() || '';

  const { uploading, error, fileInputRef, handleFileInput, openFilePicker, clearPhoto } =
    useLivreurPhotoUpload({
      onPhotoChange,
      commercantId,
      folderBase: 'instructeurs',
      disabled: disabled || saving,
    });

  const interactionDisabled = disabled || saving || uploading;

  const helpLine = (
    <p
      className={`text-gray-400 ${inline ? 'whitespace-nowrap text-[10px] leading-none' : 'text-xs'}`}
    >
      PNG, JPG ou WEBP — 5 Mo max.
    </p>
  );

  return (
    <div
      className={`flex flex-col ${inline ? 'items-center gap-1 text-center' : 'items-start gap-1.5'}`}
    >
      <div className="relative inline-flex">
        <button
          type="button"
          onClick={openFilePicker}
          disabled={interactionDisabled}
          className={`group relative rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2 ${
            interactionDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
          }`}
          aria-label="Modifier la photo du praticien"
        >
          <MemberAvatar member={member} size="xl" className="ring-2 ring-gray-100" />
          {!uploading && (
            <>
              <span className="pointer-events-none absolute inset-0 rounded-full bg-black/0 transition-colors group-hover:bg-black/25 group-focus-visible:bg-black/20" />
              <span className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-full opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                <Camera className="h-6 w-6 text-white drop-shadow-md" aria-hidden />
              </span>
            </>
          )}
          {uploading && (
            <span className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-full bg-white/80">
              <Loader2 className="h-7 w-7 animate-spin text-violet-600" aria-hidden />
            </span>
          )}
          {!uploading && (
            <span
              className="pointer-events-none absolute -bottom-0.5 -right-0.5 flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-violet-600 text-white shadow-sm"
              aria-hidden
            >
              <Camera className="h-3.5 w-3.5" />
            </span>
          )}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileInput}
          className="hidden"
          disabled={interactionDisabled}
        />
      </div>

      {!inline && !uploading && !saving && (
        <p className="text-xs text-gray-500">Cliquer pour modifier</p>
      )}
      {saving && !uploading && (
        <p className="text-xs font-medium leading-none text-violet-700">Enregistrement…</p>
      )}
      {uploading && (
        <p className="text-xs leading-none text-gray-500">Upload…</p>
      )}

      {inline ? (
        <div className="flex flex-col items-center gap-0.5">
          {professionalPhoto ? (
            <button
              type="button"
              onClick={clearPhoto}
              disabled={interactionDisabled}
              className="whitespace-nowrap text-[11px] leading-none text-gray-500 underline-offset-2 hover:text-rose-600 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
            >
              Supprimer la photo
            </button>
          ) : null}
          {helpLine}
        </div>
      ) : (
        <>
          {professionalPhoto ? (
            <button
              type="button"
              onClick={clearPhoto}
              disabled={interactionDisabled}
              className="text-xs text-gray-500 underline-offset-2 hover:text-rose-600 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
            >
              Supprimer la photo
            </button>
          ) : null}
          {helpLine}
        </>
      )}

      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
